import React, { useCallback, useContext, useEffect } from "react";
import { useHistory } from "react-router-dom";
import NDRE from "assets/images/NDRE.png";
import THERMAL from "assets/images/THERMAL.png";
import NDVI from "assets/images/NDVI.png";
import PCD from "assets/images/PCD.png";
import HIGHRES from "assets/images/highres.png";
import ZONING from "assets/images/zoning.png";
import STACK from "assets/images/stack.png";
import YIELD from "assets/images/yield.png";
import VRT from "assets/images/vrt.png";
import NDVIR from "assets/images/NDVI-R.png";
import COLOUR from "assets/images/colour.png";
import RAPID from "assets/images/rapid.png";
import Slider from "react-slick";
import HIGHRESSLIDER from "assets/images/highres-slider.png";
import STACKSLIDER from "assets/images/stack-slider.png";
import {
  Crosshair,
  Cube,
  Faders,
  Info,
  MapTrifold,
  CheckSquareOffset,
} from "phosphor-react";
import { getAdvancedPricing } from "api/Stripe";
import { UserContext } from "context/user";
import { StripeActions, StripeContext } from "context/stripe";
import ContentLoader from "components/ContentLoader";
import Button from "components/Button";
import usePremium from "hooks/usePremium";
import { getPricing } from "utils";
import CalculationsCard from "../components/calculationsCard";
import ProductCard from "../components/productCard";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./index.scss";

const StorePageContainer = () => {
  const history = useHistory();
  const { isPremium } = usePremium();
  const { token } = useContext(UserContext);
  const { state: stripeState, dispatch } = useContext(StripeContext);
  const { pricingDetails, isFetchingPricingDetails } = stripeState;

  const settings = {
    customPaging(i) {
      if (i === 0) {
        return <div className="prepare">Prepare Soil</div>;
      }
      if (i === 1) {
        return <div className="grow">Grow & Monitor</div>;
      }
      if (i === 2) {
        return <div className="prescribe">Prescribe & VRT</div>;
      }
      return <div className="harvest">Harvest & Review</div>;
    },
    dots: true,
    dotsClass: "slick-dots slick-thumb",
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    fade: true,
    autoplay: true,
    autoplaySpeed: 8000,
  };
  const fetchPrice = useCallback(async () => {
    if (!token) return;
    dispatch({
      type: StripeActions.FETCH_PRICING_DETAILS,
    });
    try {
      const payload = await getAdvancedPricing(token);
      dispatch({
        type: StripeActions.FETCH_PRICING_DETAILS_SUCCESSFUL,
        value: payload,
      });
    } catch (error) {
      dispatch({
        type: StripeActions.FETCH_PRICING_DETAILS_FAIL,
      });
    }
  }, [dispatch, token]);

  useEffect(() => {
    fetchPrice();
  }, [fetchPrice]);

  const { actualPrice, basePrice, discountValue, isValidDiscount } =
    getPricing(pricingDetails);

  const renderPrice = () => {
    return <h3 className="price">${basePrice} AUD</h3>;
  };

  const renderDiscountedPrice = () => {
    return (
      <>
        <h3 className="price">${actualPrice} AUD</h3>
        {basePrice && <span className="append"> from ${basePrice}</span>}
        {discountValue && (
          <p className="discount">{`${discountValue}`} Discount offer</p>
        )}
      </>
    );
  };

  return (
    <div data-testid="store-page" className="store-page">
      <section className="header-section">
        <h1 className="title">Store</h1>
      </section>
      <section className="pricing-section">
        <div className="pricing-wrapper">
          <h2 className="title">Subscription Options</h2>
          <div className={`pricing-item ${!isPremium ? "-premium" : ""}`}>
            <h6 className="tier-level">Basic Account</h6>
            <p className="tier-description">Perfect for starters.</p>
            <div className="price-wrapper">
              <h3 className="price">Free</h3>
              <p className="append">forever, don&lsquo;t worry</p>
            </div>
            <button
              className="button"
              type="button"
              disabled={!isPremium}
              onClick={() => history.push("/settings#subscription-status")}
            >
              {isPremium ? "Switch to Basic" : "Current Plan"}
            </button>
            <ul className="feature-list">
              <li className="list-item">
                <CheckSquareOffset
                  size={32}
                  color={!isPremium ? "#292b33" : "#ffffff"}
                />
                <p className="item">Create farm map and draw fields.</p>
              </li>
              <li className="list-item">
                <CheckSquareOffset
                  size={32}
                  color={!isPremium ? "#292b33" : "#ffffff"}
                />
                <p className="item">Collaborate and share farms.</p>
              </li>
              <li className="list-item">
                <CheckSquareOffset
                  size={32}
                  color={!isPremium ? "#292b33" : "#ffffff"}
                />
                <p className="item">Prioritise your farms with favourites.</p>
              </li>
              <li className="list-item">
                <CheckSquareOffset
                  size={32}
                  color={!isPremium ? "#292b33" : "#ffffff"}
                />
                <p className="item">Calculate distance and area.</p>
              </li>
              <li className="list-item">
                <CheckSquareOffset
                  size={32}
                  color={!isPremium ? "#292b33" : "#ffffff"}
                />
                <p className="item">
                  Locate your position in the field with the GPS.
                </p>
              </li>
              <li className="list-item">
                <CheckSquareOffset
                  size={32}
                  color={!isPremium ? "#292b33" : "#ffffff"}
                />
                <p className="item">
                  NDVI (10m): Good for general crop biomass & health.
                </p>
              </li>
              <li className="list-item">
                <CheckSquareOffset
                  size={32}
                  color={!isPremium ? "#292b33" : "#ffffff"}
                />
                <p className="item">
                  NDVI-R (10m): Regional perspective of crop biomass & health.
                </p>
              </li>
            </ul>
          </div>
          <div className={`pricing-item ${isPremium ? "-premium" : ""}`}>
            <h6 className="tier-level">Advanced Account</h6>
            <p className="tier-description">For users who want to do more.</p>
            <div className="price-wrapper">
              {isFetchingPricingDetails && <ContentLoader />}
              {!isFetchingPricingDetails && (
                <>{isValidDiscount ? renderDiscountedPrice() : renderPrice()}</>
              )}
            </div>
            <button
              className="button"
              type="button"
              disabled={isPremium}
              onClick={() => history.push("/advanced/subscribe")}
            >
              {isPremium ? "Current Plan" : "Subscribe to Advanced"}
            </button>
            <ul className="feature-list">
              <li className="list-item">
                <CheckSquareOffset
                  size={32}
                  color={isPremium ? "#606470" : "#ffffff"}
                />
                <p className="item">All from Basic Features.</p>
              </li>
              <li className="list-item">
                <CheckSquareOffset
                  size={32}
                  color={isPremium ? "#606470" : "#ffffff"}
                />
                <p className="item">
                  15% off on all Zoning VRT and Stack Analysis orders.
                </p>
              </li>
              <li className="list-item">
                <CheckSquareOffset
                  size={32}
                  color={isPremium ? "#606470" : "#ffffff"}
                />
                <p className="item">View multiple fields of imagery at once.</p>
              </li>
              <li className="list-item">
                <CheckSquareOffset
                  size={32}
                  color={isPremium ? "#606470" : "#ffffff"}
                />
                <p className="item">
                  PCD Imagery Calculations for horticulture + moisture stressed
                  vegetation.
                </p>
              </li>
              <li className="list-item">
                <CheckSquareOffset
                  size={32}
                  color={isPremium ? "#606470" : "#ffffff"}
                />
                <p className="item">
                  NDRE Imagery Calculations for high biomass crops such as
                  cotton, rice, sugar + nitrogen levels.
                </p>
              </li>
              <li className="list-item">
                <CheckSquareOffset
                  size={32}
                  color={isPremium ? "#606470" : "#ffffff"}
                />
                <p className="item">
                  Thermal Imagery Calculations that detects water stress in
                  plants, great for irrigation management.
                </p>
              </li>
            </ul>
          </div>
        </div>
      </section>
      <section className="products-section">
        <h2 className="products-title">Explore Datafarming Products</h2>
        <ProductCard
          imgSrc={HIGHRES}
          title="High Resolution Satellite Imagery"
          description="Ideal for monitoring crop variability and performance in fine detail, high resolution imagery gives you greater confidence in detecting issues that are difficult to see at ground level. Satellites are more cost effective than drones or planes, and data can be gathered over anywhere in the world."
          buttonText="Order Imagery"
          productAction="/order/high-resolution-imagery"
        />
        <ProductCard
          imgSrc={RAPID}
          title="Rapid-EM™"
          description="Rapid-EM™ uses ElectroMagnetic technology, matched with a simple app in the cab, to identify soil variability down to 1.5 metres into the ground. It specifically measures clay content, salts, and moisture. Rapid-EM™ is used to zone paddocks according to soil type and potential yield; with machines available for hire anywhere in Australia."
          buttonText="Book Rapid-EM™"
          productAction="/rapid-em-booking"
        />
        <ProductCard
          imgSrc={VRT}
          title="Variable Rate Application"
          description="Use satellite imagery to match product rate to crop growth using our rapid zoning tool. Within 5 minutes you can select and image, zone it, and receive a file for the tractor, sprayer, or spreader. Be more efficient, save money, and farm more sustainably as well."
          comingSoon
        />
        <ProductCard
          imgSrc={STACK}
          title="Stack"
          description="Identify long term variability and performance across fields and target them for action. Ideal for preparing your soils for planting."
          comingSoon
        />
        <ProductCard
          imgSrc={YIELD}
          title="Yield Analysis"
          description="Yield is the ultimate measure of farm performance, but processing yield maps is difficult and time consuming. Our yield analysis service takes your raw data and presents maps that are easy to understand in interpret. Also check how your trials performed. Compare against other layers."
          comingSoon
        />
      </section>

      <section className="slider-section">
        <div className="slider-wrapper">
          <h2 className="slider-title">
            Looking for more ways to understand your farm?
          </h2>
          <Slider {...settings}>
            <div className="slider-item -green">
              <div className="slider-content">
                <div className="pill -green">
                  <Info size={20} weight="bold" color="#09ba51" />
                  Ideal for Prepare Phase
                </div>
                <div className="label-wrapper">
                  <div className="icon-container">
                    <MapTrifold size={24} color="#ffffff" />
                  </div>
                  <p className="label">STACK</p>
                </div>
                <h2 className="title">
                  Identify optimal soil locations prior to planting
                </h2>
                <p className="description">
                  STACK combines multiple years of satellite imagery. It is
                  ideal for identifying long term variability and performance
                  across fields and targeting them for action. It is a great
                  tool for identifying soil sampling locations, and fixing soil
                  constraints like acidity and hard setting (sodicity).
                </p>
                <a
                  className="coming-soon"
                  href="mailto:support@datafarming.com.au"
                >
                  Contact Us
                </a>
              </div>
              <div className="mockup-photo">
                <img src={STACK} alt="slider" />
              </div>
              <div className="image-wrapper">
                <img src={STACK} alt="slider" />
              </div>
            </div>
            <div className="slider-item -blue">
              <div className="slider-content">
                <div className="pill -blue">
                  <Info size={20} weight="bold" color="#0c83f1" />
                  Ideal for Growing Phase
                </div>
                <div className="label-wrapper">
                  <div className="icon-container">
                    <Crosshair size={24} color="#ffffff" />
                  </div>
                  <p className="label">High Resolution Imagery</p>
                </div>
                <h2 className="title">
                  Detect the hard-to-spot issues before they become a problem
                </h2>
                <p className="description">
                  Ideal for monitoring in-season crop variability and
                  performance, high resolution satellite imagery can detect
                  issues that are difficult to spot at ground-level.
                </p>
                <div className="button-group">
                  <Button
                    color="blue"
                    type="solid"
                    label="Order Now"
                    onClick={() =>
                      history.push("/order/high-resolution-imagery")
                    }
                  />
                  <Button
                    color="white"
                    type="solid"
                    label="Learn More"
                    onClick={() =>
                      history.push("/store/high-resolution-imagery")
                    }
                  />
                </div>
              </div>
              <div className="mockup-photo">
                <img src={HIGHRES} alt="slider" />
              </div>
              <div className="image-wrapper">
                <img src={HIGHRESSLIDER} alt="slider" />
              </div>
            </div>
            <div className="slider-item -orange">
              <div className="slider-content">
                <div className="pill -orange">
                  <Info size={20} weight="bold" color="#f7901e" />
                  Ideal for Prescribe Phase
                </div>
                <div className="label-wrapper">
                  <div className="icon-container">
                    <Faders size={24} color="#ffffff" />
                  </div>
                  <p className="label">Variable Rate Application</p>
                </div>
                <h2 className="title">
                  Use the right amount of fertiliser at the right time, in the
                  right place
                </h2>
                <p className="description">
                  Use satellite imagery to match product rate to crop growth
                  using our rapid zoning tool. Within 5 minutes you can select
                  and image, zone it, and receive a file for the tractor,
                  sprayer, or spreader. Be more efficient, save money, and farm
                  more sustainably as well.
                </p>
                <a
                  className="coming-soon"
                  href="mailto:support@datafarming.com.au"
                >
                  Contact Us
                </a>
              </div>
              <div className="mockup-photo">
                <img src={ZONING} alt="slider" />
              </div>
              <div className="image-wrapper">
                <img src={STACKSLIDER} alt="slider" />
              </div>
            </div>
            <div className="slider-item -black">
              <div className="slider-content">
                <div className="pill -black">
                  <Info size={20} weight="bold" color="#292b33" />
                  Ideal for Harvest Phase
                </div>
                <div className="label-wrapper">
                  <div className="icon-container">
                    <Cube size={24} color="#ffffff" />
                  </div>
                  <p className="label">Yield Analysis</p>
                </div>
                <h2 className="title">
                  Review and make changes for the next season
                </h2>
                <p className="description">
                  Assess your final performance with our yield data processing
                  service.
                </p>
                <a
                  className="coming-soon"
                  href="mailto:support@datafarming.com.au"
                >
                  Contact Us
                </a>
              </div>
              <div className="mockup-photo">
                <img src={YIELD} alt="slider" />
              </div>
              <div className="image-wrapper">
                <img src={STACKSLIDER} alt="slider" />
              </div>
            </div>
          </Slider>
        </div>
      </section>
      <section className="calculations-section">
        <div className="calculation-wrapper">
          <div className="headline-wrapper">
            <p className="label">
              Optimize each step of the growing cycle using our state of the art
              technology.
            </p>
            <h2 className="title">Exploring Datafarming Calculations</h2>
          </div>
          <CalculationsCard
            imgSrc={NDVI}
            title="Normalized Difference Vegetation Index"
            description="Recognised across all crop and pasture industries. NDVI is the most widely used algorithm, and is suitable for most situations."
          />
          <CalculationsCard
            imgSrc={NDVIR}
            title="Regional NDVI"
            description="NDVI Regional is a way to view your crops across whole farms and across the region.  It allows you to compare crops planted at similar times, to identify which ones may have growth issues.   "
          />
          <CalculationsCard
            imgSrc={COLOUR}
            title="Colour"
            description="Always use the colour image layer to check for cloud and cloud shadow.  The presence of cloud and cloud shadow can make the crop appear poor, when it may not be."
          />
          <h3 className="sub-title">and do more with Advanced account</h3>
          <CalculationsCard
            title="Normalized Difference Red Edge Index"
            imgSrc={NDRE}
            description="Recognised across rice, cotton, and other cereals. NDRE excels during the mid-late cropping season where plants reach full growth; NDRE continues to show variability, while NDVI saturates."
            advanced={!isPremium}
          />
          <CalculationsCard
            title="Plant Cell Density"
            imgSrc={PCD}
            description="Recognised across the viticulture and cotton sectors.  PCD excels during the mid-late cropping season where plants reach full growth and might have moisture stress; PCD continues to show variability, while NDVI saturates."
            advanced={!isPremium}
          />
          <CalculationsCard
            title="Thermal"
            imgSrc={THERMAL}
            description="Thermal reflectance imagery is a relative new comer to crop and pasture management. Whilst this layer doesn’t provide absolute temperature, it detects plant stresses with thermal surface reflectance. Useful in irrigation situations."
            advanced={!isPremium}
          />
        </div>
      </section>
    </div>
  );
};

export default StorePageContainer;
